import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import ErrorMessage from '../../Elements/ErrorMessage';
import FaIcon from '../../Elements/FaIcon';
import LoadingIcon from '../../Elements/LoadingIcon';
import dsSelectStyles from '../../Elements/InputComponents/BaseInputs/Selectors/reactSelectDsStyles';

const ListSelector = (props) => {
  const {
    resource,
    selectedId,
    selectedLabel,
    clientIdFilter,
    projectIdFilter,
    tpProjectIdFilter,
    opportunityIdFilter,
    surveyIdFilter,
    tpSurveyIdFilter,
    loadFunction,
    preloadedList,
    closeSelectorPopup,
    selectedResourceChange,
  } = props;

  const [resourceList, setResourceList] = useState([]);
  const [currentSelectedId, setCurrentSelectedId] = useState(selectedId);
  const [currentSelectedLabel, setCurrentSelectedLabel] = useState(selectedLabel);
  const [allFilteredOut, setAllFilteredOut] = useState(false);

  const selectLinkRef = useRef(null);

  // Load resource list on mount and when filters change. A caller that already holds
  // the list (e.g. the entry editor prefetches the user's IC types) passes it as
  // preloadedList to skip the click-time fetch.
  useEffect(() => {
    let isMounted = true;

    const listPromise = preloadedList
      ? Promise.resolve(preloadedList)
      : loadFunction(
          null,
          clientIdFilter,
          projectIdFilter,
          opportunityIdFilter,
          surveyIdFilter,
          tpProjectIdFilter,
          tpSurveyIdFilter
        );

    listPromise
      .then((result) => {
        if (!isMounted) return;
        if (result.length === 0) {
          setResourceList([
            {
              label: '',
              value: resource === 'servicetype' ? '' : null,
            },
          ]);
          setAllFilteredOut(true);
          return;
        }
        setAllFilteredOut(false);
        let newResourceList;
        switch (resource) {
          case 'lead':
            newResourceList = [{ label: '', value: -1 }].concat(
              result.map((r) => ({
                label: `${r.companyName} (${r.leadId})`,
                value: r.leadId,
              }))
            );
            break;
          case 'client':
            newResourceList = [{ label: '', value: null }].concat(
              result.map((r) => ({
                label: `${r.name} (${r.clientId})`,
                value: r.clientId,
              }))
            );
            break;
          case 'project':
            newResourceList = [{ label: '', value: null }].concat(
              result.map((r) => ({
                label: `${r.cfName} (${r.cfId})`,
                value: r.cfId,
              }))
            );
            break;
          case 'tpproject':
            newResourceList = [{ label: '', value: null }].concat(
              result.map((r) => ({
                label: `${r.projectName} (${r.projectId})`,
                value: r.projectId,
              }))
            );
            break;
          case 'opportunity':
            newResourceList = [{ label: '', value: null }].concat(
              result.map((r) => ({
                label: `${r.name} (${r.opportunityId})`,
                value: r.opportunityId,
              }))
            );
            break;
          case 'survey':
            newResourceList = [{ label: '', value: null }].concat(
              result.map((r) => ({
                label: `${r.name} (${r.sassieId})`,
                value: r.surveyId,
              }))
            );
            break;
          case 'tpsurvey':
            newResourceList = [{ label: '', value: null }].concat(
              result.map((r) => ({
                label: `${r.thirdPartySurveyName} (${r.tpSurveyId})`,
                value: r.tpSurveyId,
              }))
            );
            break;
          case 'servicetype':
            newResourceList = [{ label: '', value: '' }].concat(result.map((r) => ({ label: r, value: r })));
            break;
          default:
            newResourceList = [];
            break;
        }
        setResourceList(newResourceList);
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('LS71', error);
      });
    return () => {
      isMounted = false;
    };
  }, [
    resource,
    clientIdFilter,
    projectIdFilter,
    tpProjectIdFilter,
    opportunityIdFilter,
    surveyIdFilter,
    tpSurveyIdFilter,
    loadFunction,
    preloadedList,
  ]);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectLinkRef.current && !selectLinkRef.current.contains(event.target)) {
        closeSelectorPopup();
      }
    };
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, [closeSelectorPopup]);

  const onSelectedResourceChange = useCallback(
    (e) => {
      e.resourceType = resource;
      setCurrentSelectedId(e.value);
      setCurrentSelectedLabel(e.label);
      selectedResourceChange(e);
    },
    [resource, selectedResourceChange]
  );

  let ConstructedSelectComponent;
  if (resourceList.length > 1) {
    if (currentSelectedId == null || currentSelectedId === -1) {
      ConstructedSelectComponent = (
        <Select
          options={resourceList}
          onChange={onSelectedResourceChange}
          placeholder={'Please select'}
          className='pointer'
          styles={dsSelectStyles}
          menuPlacement='auto'
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          // Fixed, as in MultiChoice: an 'absolute' portalled menu can loop with the page scroll (CD-5713).
          menuPosition='fixed'
        />
      );
    } else {
      ConstructedSelectComponent = (
        <Select
          options={resourceList}
          onChange={onSelectedResourceChange}
          value={{
            label: currentSelectedLabel,
            value: currentSelectedId,
          }}
          className='pointer'
          styles={dsSelectStyles}
          menuPlacement='auto'
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition='fixed'
        />
      );
    }
  } else if (allFilteredOut) {
    ConstructedSelectComponent = (
      <Select
        options={resourceList}
        onChange={onSelectedResourceChange}
        placeholder={'No item found with the current filters.'}
        className='pointer'
        styles={dsSelectStyles}
        menuPlacement='auto'
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition='fixed'
      />
    );
  } else {
    ConstructedSelectComponent = (
      <Select
        options={resourceList}
        onChange={onSelectedResourceChange}
        placeholder={
          <span className='h4'>
            <LoadingIcon
              loading={true}
              className=''
            />
          </span>
        }
        className='pointer'
        styles={dsSelectStyles}
        menuPlacement='auto'
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition='fixed'
        defaultMenuIsOpen={true}
        autoFocus={true}
      />
    );
  }

  return (
    <div
      ref={selectLinkRef}
      className='ls-picker'
    >
      <div className='ls-select'>{ConstructedSelectComponent}</div>
      <button
        type='button'
        className='ls-clear'
        aria-label='Clear'
        title='Clear'
        onClick={() =>
          onSelectedResourceChange({
            label: '',
            value: resource === 'servicetype' ? '' : null,
          })
        }
      >
        <FaIcon iconName='fa-solid fa-xmark' />
      </button>
    </div>
  );
};

ListSelector.defaultProps = {};

export default ListSelector;
